import { asset } from '../config';
import { measureTrack, simplifyForStorage, type Coord } from '../design/geo';
import { cleanCoords, uid, type Route } from '../design/schema';
import { exchangeStravaCode } from './api';

export interface StravaActivity {
  id: number;
  name: string;
  type: string;
  start_date_local: string;
  distance: number;
  total_elevation_gain: number;
  moving_time: number;
  map?: { summary_polyline?: string | null };
}

const callbackUrl = () => new URL(asset('strava-callback.html'), location.href).toString();

/** Opens Strava's consent screen in a popup and resolves with a short-lived access token. */
export function connectStrava(clientId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const state = uid();
    const url =
      `https://www.strava.com/oauth/authorize?client_id=${encodeURIComponent(clientId)}` +
      `&response_type=code&redirect_uri=${encodeURIComponent(callbackUrl())}` +
      `&approval_prompt=force&scope=read,activity:read_all&state=${state}`;
    const w = 600, h = 720;
    const popup = window.open(url, 'tm-strava', `width=${w},height=${h},left=${window.screenX + (window.outerWidth - w) / 2},top=${window.screenY + (window.outerHeight - h) / 3}`);
    if (!popup) return reject(new Error('Your browser blocked the Strava window. Allow pop-ups for this site and try again.'));

    const done = (fn: () => void) => {
      window.removeEventListener('message', onMessage);
      clearInterval(closedTimer);
      fn();
    };
    const onMessage = (e: MessageEvent) => {
      const data = e.data;
      if (e.origin !== new URL(callbackUrl()).origin || data?.source !== 'tm-strava' || data.state !== state) return;
      if (data.error || !data.code) return done(() => reject(new Error('Strava access was not granted.')));
      // Strava says which permissions were actually ticked; without activity access every call is refused.
      if (typeof data.scope === 'string' && !/activity:read/.test(data.scope)) {
        return done(() => reject(new Error("Strava connected, but without permission to read your activities. Connect again and leave 'View data about your activities' ticked.")));
      }
      done(() => exchangeStravaCode(data.code).then((r) => resolve(r.accessToken), reject));
    };
    const closedTimer = setInterval(() => {
      if (popup.closed) done(() => reject(new Error('CANCELLED')));
    }, 600);
    window.addEventListener('message', onMessage);
  });
}

async function strava<T>(token: string, path: string): Promise<T> {
  const res = await fetch(`https://www.strava.com/api/v3${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (res.status === 401) throw new Error('Your Strava session expired. Connect again.');
  if (res.status === 429) throw new Error('Strava is rate-limiting requests right now. Try again in a few minutes.');
  if (!res.ok) {
    // Pass Strava's own reason on, e.g. "activity:read_permission missing".
    const body = await res.json().catch(() => null);
    const detail = body?.errors?.map((e: any) => [e.resource, e.field, e.code].filter(Boolean).join(' ')).join('; ') || body?.message;
    throw new Error(`Strava refused the request (${res.status}${detail ? `: ${detail}` : ''}).`);
  }
  return res.json();
}

export const listActivities = (token: string, page = 1) =>
  strava<StravaActivity[]>(token, `/athlete/activities?per_page=30&page=${page}`).then((list) =>
    list.filter((a) => a.map?.summary_polyline),
  );

/** Full-resolution track with elevation (the activity list only carries a coarse polyline). */
export async function importActivity(token: string, activity: StravaActivity): Promise<Route> {
  const streams = await strava<Record<string, { data: any[] }>>(
    token,
    `/activities/${activity.id}/streams?keys=latlng,altitude&key_by_type=true`,
  );
  const latlng: [number, number][] = streams.latlng?.data ?? [];
  const altitude: number[] = streams.altitude?.data ?? [];
  if (latlng.length < 2) throw new Error(`"${activity.name}" has no GPS track.`);
  const coords: Coord[] = latlng.map((p, i) => [p[1], p[0], Number.isFinite(altitude[i]) ? altitude[i] : null]);
  const full = cleanCoords(coords);
  return { id: uid(), name: activity.name, source: 'strava', coords: simplifyForStorage(full), movingTime: activity.moving_time ?? null, ...measureTrack(full) };
}
